import {cn} from '@sollapay/ui';

import type {useAiFillFlash, useFieldAiProps} from '../hooks';
import type {AiFieldAccessor} from '../types';
import type {ReactNode} from 'react';

export type AiFlashWrapperProps =
  | {
      field: string;
      fieldAi: ReturnType<typeof useFieldAiProps>;
      isFlashing: ReturnType<typeof useAiFillFlash>;
      children: (ai: AiFieldAccessor) => ReactNode;
      className?: string;
    }
  | {
      /** Precomputed flash state — for a badge/wrapper derived from more than one field's
       *  `ai` (e.g. a cross-referenced badge, or a child component fed two fields' `ai` as
       *  props), where a single `field` can't drive both the flash state and the children. */
      flashing: boolean;
      children: ReactNode;
      className?: string;
    };

/**
 * Centralizes the wrapper markup repeated at every AI-extractable field: the brief
 * flash-on-fill highlight (`useAiFillFlash`) plus, in the common single-field case, a single
 * `fieldAi(field)` call handed to the caller as `{aiIndicator, aiGenerating, onAiChange}`.
 */
export function AiFlashWrapper(props: AiFlashWrapperProps) {
  const isFlashing = 'flashing' in props ? props.flashing : props.isFlashing(props.field);
  const content = 'flashing' in props ? props.children : props.children(props.fieldAi(props.field));

  return (
    <div className={cn(props.className, isFlashing && 'animate-ai-fill-flash rounded-2xl')}>
      {content}
    </div>
  );
}
